import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import EffectiveDateField, { isValidYmd } from "./EffectiveDateField";
import styles from "./HistoryCorrectionModal.module.css";

export type HistoryCorrectionMode = "change-date" | "delete";

type HistoryCorrectionModalProps = {
  show: boolean;
  mode: HistoryCorrectionMode;
  title?: string;
  rowSummary?: string;
  currentEffectiveDate?: string;
  saving?: boolean;
  errorMessage?: string;
  conflictEffectiveDate?: string;
  onCancel: () => void;
  onConfirmChangeDate: (effectiveStartDate: string) => void;
  onConfirmDelete: () => void;
};

const WARNING_COPY =
  "This correction changes how future report reruns resolve historical values. Existing generated invoice snapshots will not change unless the invoice is deleted and regenerated.";

const HistoryCorrectionModal = ({
  show,
  mode,
  title,
  rowSummary,
  currentEffectiveDate,
  saving = false,
  errorMessage,
  conflictEffectiveDate = "",
  onCancel,
  onConfirmChangeDate,
  onConfirmDelete,
}: HistoryCorrectionModalProps) => {
  const [effectiveDate, setEffectiveDate] = useState("");
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    if (!show) return;
    setEffectiveDate(currentEffectiveDate || "");
    setDateError("");
  }, [show, currentEffectiveDate]);

  const titleId = "historyCorrectionTitle";
  const errorId = "historyCorrectionError";
  const isDelete = mode === "delete";
  const hasDateConflict = !isDelete && Boolean(conflictEffectiveDate) && effectiveDate === conflictEffectiveDate;
  const visibleErrorMessage = conflictEffectiveDate && effectiveDate !== conflictEffectiveDate ? "" : errorMessage;

  const handleConfirm = () => {
    if (isDelete) {
      onConfirmDelete();
      return;
    }

    if (!effectiveDate) {
      setDateError("Select a new effective date.");
      return;
    }
    if (!isValidYmd(effectiveDate)) {
      setDateError("Use a valid effective date.");
      return;
    }
    onConfirmChangeDate(effectiveDate);
  };

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onCancel}
      centered
      backdrop="static"
      dialogClassName={styles["history-correction-modal"]}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="history-correction-modal"
    >
      <Modal.Header closeButton={!saving} className={styles["history-correction-header"]}>
        <Modal.Title id={titleId} className={styles["history-correction-title"]}>
          {title || (isDelete ? "Delete History Row" : "Change Effective Date")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles["history-correction-body"]}>
        <p className={styles["history-correction-copy"]}>
          {isDelete
            ? "Use this only when the row should not exist. If the value is correct but the date is wrong, change the effective date instead."
            : "Use this when the value is correct but the effective date is wrong."}
        </p>

        {rowSummary && (
          <div className={styles["history-correction-summary"]} data-testid="history-correction-summary">
            <strong>Row:</strong> {rowSummary}
          </div>
        )}

        <div className={styles["history-correction-warning"]}>{WARNING_COPY}</div>

        {!isDelete && (
          <EffectiveDateField
            id="historyCorrectionEffectiveDate"
            label="New effective date"
            value={effectiveDate}
            onChange={(value) => {
              setEffectiveDate(value);
              setDateError("");
            }}
            disabled={saving}
            data-testid="history-correction-date-input"
          />
        )}

        {(dateError || visibleErrorMessage) && (
          <div id={errorId} className={styles["history-correction-error"]} role="alert">
            {dateError || visibleErrorMessage}
          </div>
        )}
        {hasDateConflict && (
          <div className={styles["history-correction-guidance"]}>
            Pick a different effective date to continue from this modal.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className={styles["history-correction-footer"]}>
        <button
          type="button"
          className={styles["history-correction-btn-secondary"]}
          onClick={onCancel}
          disabled={saving}
          data-testid="history-correction-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          className={isDelete ? styles["history-correction-btn-danger"] : styles["history-correction-btn"]}
          onClick={handleConfirm}
          disabled={saving || hasDateConflict}
          aria-describedby={dateError || visibleErrorMessage ? errorId : undefined}
          data-testid="history-correction-confirm"
        >
          {saving ? "Saving..." : isDelete ? "Delete History Row" : "Change Date"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default HistoryCorrectionModal;
